import React from "react";
import styles from "../SiteCreationComp/AdminPreview.module.css";
import { UPLOAD_TYPE } from "./uploadTypeEnum";

const VerificationResultModal = ({ isOpen, onClose, verifications, uploadType }) => {
	if (!isOpen || !verifications) return null;

	const successes = verifications.successes || [];
	const errors = verifications.errors || [];
	const summaryMessage = verifications.message || "";

	const isReinsert = uploadType === UPLOAD_TYPE.REINSERT;
	const isIrradiance = uploadType === UPLOAD_TYPE.IRRADIANCE;

	/**
	 * Get cell background color based on upload type and match status.
	 * - DAILY_SOLAR / INDEXES / IRRADIANCE: RED for mismatch, GREEN for match
	 * - REINSERT: GREEN for mismatch (changed), inherit for match (unchanged)
	 */
	const getCellColor = (match) => {
		if (match === null || match === undefined) return "inherit";
		if (isReinsert) {
			return match ? "inherit" : "#d4edda";
		}
		return match ? "#d4edda" : "#f8d7da";
	};

	/**
	 * Build a field→match map from verification comparisons.
	 */
	const buildMatchMap = (verification) => {
		const map = {};
		if (verification?.comparisons) {
			verification.comparisons.forEach((c) => {
				map[c.field] = c.match;
			});
		}
		return map;
	};

	/**
	 * Get the display value for a cell.
	 * For others: show csvData value.
	 */
	const getCellValue = (col, item, matchMap) => {
		const val = item.csvData?.[col];
		return val !== undefined && val !== null ? String(val) : "";
	};

	/**
	 * Determine table columns from items. Collects the union of all items'
	 * `columns` arrays (preserving insertion order) so that rows with different
	 * column sets (e.g. EDMI vs non-EDMI indexes) are all represented.
	 * Falls back to collecting unique column names from csvData keys.
	 */
	const getColumns = (items) => {
		const colSet = new Set();
		let hasColumns = false;
		for (const item of items) {
			if (item.columns && item.columns.length) {
				hasColumns = true;
				item.columns.forEach((c) => colSet.add(c));
			}
		}
		if (hasColumns) return Array.from(colSet);
		// Fallback: collect from csvData keys
		items.forEach((item) => {
			if (item.csvData) Object.keys(item.csvData).forEach((k) => colSet.add(k));
		});
		return Array.from(colSet);
	};

	/**
	 * Determine which columns are "compared fields" (have verification match info).
	 */
	const getComparedFields = (items) => {
		const fieldSet = new Set();
		items.forEach((item) => {
			if (item.verification?.comparisons) {
				item.verification.comparisons.forEach((c) => fieldSet.add(c.field));
			}
			// Also check subRows (irradiance)
			if (item.subRows) {
				item.subRows.forEach((sr) => {
					if (sr.verification?.comparisons) {
						sr.verification.comparisons.forEach((c) => fieldSet.add(c.field));
					}
				});
			}
		});
		return fieldSet;
	};

	/**
	 * Render status text + color for a row
	 */
	const getStatusInfo = (item, isError) => {
		if (isError) return { text: item.error || "Error", bg: "#f8d7da" };
		if (isReinsert) {
			const hasChanges = item.verification?.allMatch === false;
			return { text: hasChanges ? "Data changed" : "No change", bg: hasChanges ? "#d4edda" : "inherit" };
		}
		const allMatch = item.verification?.allMatch;
		return { text: allMatch === false ? "Update not effective" : "OK", bg: allMatch === false ? "#f8d7da" : "#d4edda" };
	};

	/**
	 * Render the successes table with verification/comparison coloring.
	 * For IRRADIANCE: each success item may have subRows (one per date in range),
	 * which are expanded inline as additional table rows.
	 */
	const renderSuccessTable = () => {
		if (!successes.length) return null;

		// For irradiance, also gather subRow columns
		const allForColumns = [...successes];
		successes.forEach((item) => {
			if (item.subRows) item.subRows.forEach((sr) => allForColumns.push(sr));
		});

		const columns = getColumns(allForColumns);
		const comparedFields = getComparedFields(successes);

		// Build flat rows: for irradiance successes, expand subRows
		const flatRows = [];
		for (const item of successes) {
			if (isIrradiance && item.subRows && item.subRows.length) {
				item.subRows.forEach((sr, si) => {
					flatRows.push({
						...sr,
						_parentRow: item.row,
						_isSubRow: true,
						_subIndex: si,
						_subTotal: item.subRows.length,
					});
				});
			} else {
				flatRows.push(item);
			}
		}

		return (
			<table className={styles.compareStructureTable} style={{ width: "100%", fontSize: "12px" }}>
				<thead>
					<tr>
						<th>row</th>
						{columns.map((col) => <th key={col}>{col}</th>)}
						<th>status</th>
					</tr>
				</thead>
				<tbody>
					{flatRows.map((r, idx) => {
						const matchMap = buildMatchMap(r.verification);
						const statusInfo = getStatusInfo(r, false);
						const rowLabel = r._isSubRow
							? (r._subIndex === 0 ? String(r._parentRow) : "")
							: String(r.row);

						return (
							<tr key={idx}>
								<td>{rowLabel}</td>
								{columns.map((col) => {
									const isCompared = comparedFields.has(col);
									const cellMatch = matchMap[col];
									const bgColor = isCompared ? getCellColor(cellMatch) : "inherit";
									return (
										<td key={col} style={{ backgroundColor: bgColor, fontSize: isReinsert ? "11px" : "12px" }}>
											{getCellValue(col, r, matchMap)}
										</td>
									);
								})}
								<td style={{ backgroundColor: statusInfo.bg, fontSize: "11px" }}>
									{statusInfo.text}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		);
	};

	/**
	 * Render the errors table. Shows CSV input data + error message per row.
	 * Columns are derived from each error item's own `columns` array,
	 * which matches the CSV input fields available at the point of failure.
	 */
	const renderErrorTable = () => {
		if (!errors.length) return null;

		const columns = getColumns(errors);

		return (
			<table className={styles.compareStructureTable} style={{ width: "100%", fontSize: "12px" }}>
				<thead>
					<tr>
						<th>row</th>
						{columns.map((col) => <th key={col}>{col}</th>)}
						<th>error</th>
					</tr>
				</thead>
				<tbody>
					{errors.map((err, idx) => (
						<tr key={idx}>
							<td>{err.row}</td>
							{columns.map((col) => {
								const val = err.csvData?.[col];
								return (
									<td key={col} style={{ fontSize: "12px" }}>
										{val !== undefined && val !== null ? String(val) : ""}
									</td>
								);
							})}
							<td style={{ backgroundColor: "#f8d7da", fontSize: "11px" }}>
								{err.error || "Unknown error"}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	const totalErrors = errors.length;
	const failedUpdates = successes.filter((s) => {
		if (isReinsert) return false;
		return s.verification?.allMatch === false;
	}).length;

	return (
		<div className="overlay translucent">
			<div id="form-container" className="show">
				<div className={`form-contents ${styles.adminPreview}`} style={{ marginTop: "90px", maxHeight: "80vh", overflowY: "auto" }}>
					{/* Close button */}
					<div className="row" style={{ marginBottom: "10px" }}>
						<div className="col-sm-11">
							<h4>Upload Results</h4>
						</div>
						<div className="col-sm-1">
							<span
								onClick={onClose}
								style={{ float: "right", cursor: "pointer" }}
								className="glyphicon glyphicon-remove close-approval"
							></span>
						</div>
					</div>

					{summaryMessage && (
						<div className="alert alert-info" role="alert">{summaryMessage}</div>
					)}

					{/* Summary badges */}
					<div style={{ marginBottom: "15px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
						{totalErrors > 0 && (
							<span style={{ padding: "4px 12px", backgroundColor: "#d9534f", color: "#fff", borderRadius: "4px", fontSize: "13px" }}>
								{totalErrors} error(s)
							</span>
						)}
						{failedUpdates > 0 && (
							<span style={{ padding: "4px 12px", backgroundColor: "#f0ad4e", color: "#fff", borderRadius: "4px", fontSize: "13px" }}>
								{failedUpdates} update(s) not effective
							</span>
						)}
						{successes.length > 0 && totalErrors === 0 && failedUpdates === 0 && (
							<span style={{ padding: "4px 12px", backgroundColor: "#5cb85c", color: "#fff", borderRadius: "4px", fontSize: "13px" }}>
								All {successes.length} row(s) processed successfully
							</span>
						)}
					</div>

					{/* Legend */}
					<div style={{ marginBottom: "15px", fontSize: "12px", display: "flex", gap: "15px", flexWrap: "wrap" }}>
						{isReinsert ? (
							<>
								<span><span style={{ display: "inline-block", width: "12px", height: "12px", backgroundColor: "#d4edda", border: "1px solid #ccc", marginRight: "4px" }}></span>Data changed</span>
								<span><span style={{ display: "inline-block", width: "12px", height: "12px", backgroundColor: "inherit", border: "1px solid #ccc", marginRight: "4px" }}></span>No change</span>
								<span><span style={{ display: "inline-block", width: "12px", height: "12px", backgroundColor: "#f8d7da", border: "1px solid #ccc", marginRight: "4px" }}></span>Error</span>
							</>
						) : (
							<>
								<span><span style={{ display: "inline-block", width: "12px", height: "12px", backgroundColor: "#d4edda", border: "1px solid #ccc", marginRight: "4px" }}></span>Matched / Success</span>
								<span><span style={{ display: "inline-block", width: "12px", height: "12px", backgroundColor: "#f8d7da", border: "1px solid #ccc", marginRight: "4px" }}></span>Mismatch / Error</span>
							</>
						)}
					</div>

					<div style={{ overflowX: "auto" }}>
						{successes.length === 0 && errors.length === 0 && (
							<div style={{ fontSize: "13px" }}>No rows processed.</div>
						)}

						{successes.length > 0 && (
							<>
								<h5 style={{ marginTop: "10px", marginBottom: "8px" }}>
									Successes ({successes.length})
								</h5>
								{renderSuccessTable()}
							</>
						)}

						{errors.length > 0 && (
							<>
								<h5 style={{ marginTop: successes.length > 0 ? "20px" : "10px", marginBottom: "8px", color: "#d9534f" }}>
									Errors ({errors.length})
								</h5>
								{renderErrorTable()}
							</>
						)}
					</div>

					<div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "30px" }}>
						<button className="btn btn-primary btn-generate btn-approval" title="Close" onClick={onClose}>
							<span>OK</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VerificationResultModal;
